import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  BookOpen, Plus, Search, Edit, Trash2, Save, X, Package,
  Wine, UtensilsCrossed, Coffee, ChevronRight, Beaker, CheckCircle,
  AlertCircle, ListChecks, LayoutList
} from 'lucide-react';
import { getErrorMessage } from '../lib/utils';

import { API } from '@/lib/api';

const DEPARTMENTS = [
  { value: 'bar', label: 'Bar', icon: Wine, color: 'purple' },
  { value: 'kitchen', label: 'Kitchen', icon: UtensilsCrossed, color: 'orange' },
  { value: 'cafe', label: 'Cafe', icon: Coffee, color: 'blue' }
];

const getDepartmentBadge = (dept) => {
  const config = {
    bar: { bg: 'bg-purple-100', text: 'text-purple-800', icon: Wine },
    kitchen: { bg: 'bg-orange-100', text: 'text-orange-800', icon: UtensilsCrossed },
    cafe: { bg: 'bg-blue-100', text: 'text-blue-800', icon: Coffee }
  };
  const c = config[dept] || { bg: 'bg-gray-100', text: 'text-gray-800', icon: Package };
  const Icon = c.icon;
  return (
    <Badge className={`${c.bg} ${c.text} flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {dept?.charAt(0).toUpperCase() + dept?.slice(1)}
    </Badge>
  );
};

export default function RecipeLedger({ user }) {
  const [activeTab, setActiveTab] = useState('sale-items');
  const [loading, setLoading] = useState(true);
  
  // Data
  const [saleItems, setSaleItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [summary, setSummary] = useState({});
  
  // Filters
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [ingredientSearch, setIngredientSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  
  // Dialogs
  const [showAddItem, setShowAddItem] = useState(false);
  const [showEditItem, setShowEditItem] = useState(false);
  const [showRecipeEditor, setShowRecipeEditor] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Forms
  const [newItem, setNewItem] = useState({
    name: '',
    code: '',
    category: '',
    department: '',
    description: '',
    selling_price: ''
  });
  
  const [newCategory, setNewCategory] = useState({ name: '', department: '' });
  
  // Recipe editor state
  const [currentRecipe, setCurrentRecipe] = useState([]);
  const [newIngredient, setNewIngredient] = useState({ item_id: '', quantity: '', unit: '' });

  const canManage = user?.role === 'admin' || user?.role === 'storekeeper';

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchSaleItems();
  }, [filterDepartment, filterCategory, searchTerm]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [categoriesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/recipe-ledger/categories`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/recipe-ledger/summary`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setCategories(categoriesRes.data || []);
      setSummary(summaryRes.data || {});
      await fetchSaleItems();
      setLoading(false);
    } catch (error) {
      console.error('Recipe Ledger fetch error:', error);
      toast.error(error.message || 'Failed to fetch data', {
        action: {
          label: 'Retry',
          onClick: () => fetchData()
        }
      });
      setLoading(false);
    }
  };

  const fetchSaleItems = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (filterDepartment !== 'all') params.department = filterDepartment;
      if (filterCategory !== 'all') params.category = filterCategory;
      if (searchTerm) params.search = searchTerm;
      
      const res = await axios.get(`${API}/recipe-ledger/sale-items`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setSaleItems(res.data || []);
    } catch (error) {
      console.error('Failed to fetch sale items:', error);
    }
  };

  const searchIngredients = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const params = { query };
      if (selectedItem?.department) params.department = selectedItem.department;
      
      const res = await axios.get(`${API}/recipe-ledger/ingredients/search`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setSearchResults(res.data || []);
    } catch (error) {
      console.error('Failed to search ingredients:', error);
    }
  }, [selectedItem?.department]);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchIngredients(ingredientSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [ingredientSearch, searchIngredients]);

  const handleCreateItem = async () => {
    if (!newItem.name || !newItem.department) {
      toast.error('Name and department are required');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...newItem,
        category: newItem.category === 'none' ? '' : newItem.category,
        selling_price: newItem.selling_price ? parseFloat(newItem.selling_price) : 0
      };
      await axios.post(`${API}/recipe-ledger/sale-items`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sale item created');
      setShowAddItem(false);
      setNewItem({ name: '', code: '', category: '', department: '', description: '', selling_price: '' });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleUpdateItem = async () => {
    if (!selectedItem) return;
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...newItem,
        category: newItem.category === 'none' ? '' : newItem.category,
        selling_price: newItem.selling_price ? parseFloat(newItem.selling_price) : 0
      };
      await axios.put(`${API}/recipe-ledger/sale-items/${selectedItem.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sale item updated');
      setShowEditItem(false);
      setSelectedItem(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Delete this sale item and its recipe?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/recipe-ledger/sale-items/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sale item deleted');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const openRecipeEditor = async (item) => {
    setSelectedItem(item);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/recipe-ledger/recipes/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentRecipe(res.data?.ingredients || []);
      setShowRecipeEditor(true);
    } catch (error) {
      toast.error('Failed to load recipe');
    }
  };

  const addIngredientToRecipe = (ingredient) => {
    if (!newIngredient.quantity) {
      toast.error('Please enter quantity');
      return;
    }
    const exists = currentRecipe.find(r => r.item_id === ingredient.id);
    if (exists) {
      toast.error('Ingredient already in recipe');
      return;
    }
    setCurrentRecipe([...currentRecipe, {
      item_id: ingredient.id,
      item_name: ingredient.item_name,
      category_name: ingredient.category_name,
      super_category: ingredient.super_category,
      company: ingredient.company || '',
      quantity: parseFloat(newIngredient.quantity),
      unit: newIngredient.unit || ingredient.unit || 'pcs'
    }]);
    setNewIngredient({ item_id: '', quantity: '', unit: '' });
    setIngredientSearch('');
    setSearchResults([]);
  };

  const removeIngredientFromRecipe = (itemId) => {
    setCurrentRecipe(currentRecipe.filter(r => r.item_id !== itemId));
  };

  const updateIngredientQuantity = (itemId, quantity) => {
    setCurrentRecipe(currentRecipe.map(r => 
      r.item_id === itemId ? { ...r, quantity: parseFloat(quantity) || 0 } : r
    ));
  };

  const saveRecipe = async () => {
    if (!selectedItem) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/recipe-ledger/recipes`, {
        sale_item_id: selectedItem.id,
        ingredients: currentRecipe.map(r => ({
          item_id: r.item_id,
          quantity: r.quantity,
          unit: r.unit
        }))
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Recipe saved');
      setShowRecipeEditor(false);
      setSelectedItem(null);
      setCurrentRecipe([]);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategory.name || !newCategory.department) {
      toast.error('Name and department are required');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/recipe-ledger/categories`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { name: newCategory.name, department: newCategory.department }
      });
      toast.success('Category created');
      setShowAddCategory(false);
      setNewCategory({ name: '', department: '' });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const openEditDialog = (item) => {
    setSelectedItem(item);
    setNewItem({
      name: item.name,
      code: item.code || '',
      category: item.category || '',
      department: item.department,
      description: item.description || '',
      selling_price: item.selling_price || ''
    });
    setShowEditItem(true);
  };

  const filteredCategories = filterDepartment === 'all' 
    ? categories 
    : categories.filter(c => c.department === filterDepartment);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-blue-600" />
            Recipe Ledger
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage menu items and their ingredient recipes for automated audit
          </p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowAddCategory(true)} data-testid="add-category-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
            <Button onClick={() => setShowAddItem(true)} data-testid="add-sale-item-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Sale Item
            </Button>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Sale Items</p>
                <p className="text-2xl font-bold">{summary.total_sale_items || 0}</p>
              </div>
              <LayoutList className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">With Recipes</p>
                <p className="text-2xl font-bold text-green-600">{summary.total_recipes || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Missing Recipes</p>
                <p className="text-2xl font-bold text-orange-600">{summary.items_without_recipe || 0}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-1">
              <p className="text-sm text-gray-500">By Department</p>
              <div className="flex flex-wrap gap-1">
                {Object.entries(summary.by_department || {}).map(([dept, count]) => (
                  <Badge key={dept} variant="secondary" className="text-xs">
                    {dept}: {count}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="sale-items" data-testid="tab-sale-items">
            <LayoutList className="w-4 h-4 mr-2" />
            Sale Items
          </TabsTrigger>
          <TabsTrigger value="recipes" data-testid="tab-recipes">
            <Beaker className="w-4 h-4 mr-2" />
            Recipes
          </TabsTrigger>
        </TabsList>

        {/* Sale Items Tab */}
        <TabsContent value="sale-items" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <Label>Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search by name or code..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="search-sale-items"
                    />
                  </div>
                </div>
                <div className="w-[160px]">
                  <Label>Department</Label>
                  <Select value={filterDepartment} onValueChange={setFilterDepartment}>
                    <SelectTrigger data-testid="filter-department">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      {DEPARTMENTS.map(d => (
                        <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-[180px]">
                  <Label>Category</Label>
                  <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger data-testid="filter-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {filteredCategories.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sale Items Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Sale Items ({saleItems.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {saleItems.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No sale items found</p>
                  {canManage && (
                    <Button variant="link" onClick={() => setShowAddItem(true)}>
                      Add your first sale item
                    </Button>
                  )}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Recipe</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {saleItems.map((item) => (
                      <TableRow key={item.id} data-testid={`sale-item-row-${item.id}`}>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell className="text-gray-500">{item.code || '-'}</TableCell>
                        <TableCell>{getDepartmentBadge(item.department)}</TableCell>
                        <TableCell>{item.category || '-'}</TableCell>
                        <TableCell>
                          {item.has_recipe ? (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              {item.ingredient_count} ingredients
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-orange-600">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              No recipe
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {canManage && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openRecipeEditor(item)}
                                  data-testid={`edit-recipe-${item.id}`}
                                >
                                  <Beaker className="w-4 h-4 mr-1" />
                                  Recipe
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openEditDialog(item)}
                                  data-testid={`edit-item-${item.id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                {user?.role === 'admin' && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-600 hover:text-red-700"
                                    onClick={() => handleDeleteItem(item.id)}
                                    data-testid={`delete-item-${item.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Recipes Tab */}
        <TabsContent value="recipes" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ListChecks className="w-5 h-5" />
                Items Without Recipes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {saleItems.filter(i => !i.has_recipe).length === 0 ? (
                <div className="text-center py-8 text-green-600">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2" />
                  <p>All sale items have recipes!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {saleItems.filter(i => !i.has_recipe).map((item) => (
                    <Card key={item.id} className="border-orange-200 bg-orange-50/50">
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{item.name}</p>
                            <p className="text-sm text-gray-500">{getDepartmentBadge(item.department)}</p>
                          </div>
                          {canManage && (
                            <Button size="sm" onClick={() => openRecipeEditor(item)}>
                              <Plus className="w-4 h-4 mr-1" />
                              Add Recipe
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Beaker className="w-5 h-5" />
                Items With Recipes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {saleItems.filter(i => i.has_recipe).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Beaker className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No recipes configured yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {saleItems.filter(i => i.has_recipe).map((item) => (
                    <div 
                      key={item.id} 
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                      onClick={() => openRecipeEditor(item)}
                    >
                      <div className="flex items-center gap-3">
                        {getDepartmentBadge(item.department)}
                        <div>
                          <p className="font-medium">{item.name}</p>
                          <p className="text-sm text-gray-500">{item.code || 'No code'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-100 text-green-800">
                          {item.ingredient_count} ingredients
                        </Badge>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Sale Item Dialog */}
      <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Sale Item</DialogTitle>
            <DialogDescription>
              Add a new menu item that can be linked to ingredients
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={newItem.name}
                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                placeholder="e.g., Chicken Biryani"
                data-testid="input-item-name"
              />
            </div>
            <div>
              <Label>Code</Label>
              <Input
                value={newItem.code}
                onChange={(e) => setNewItem({ ...newItem, code: e.target.value })}
                placeholder="e.g., CB001"
                data-testid="input-item-code"
              />
            </div>
            <div>
              <Label>Department *</Label>
              <Select 
                value={newItem.department} 
                onValueChange={(v) => setNewItem({ ...newItem, department: v, category: '' })}
              >
                <SelectTrigger data-testid="select-item-department">
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {DEPARTMENTS.map(d => (
                    <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category</Label>
              <Select 
                value={newItem.category} 
                onValueChange={(v) => setNewItem({ ...newItem, category: v })}
              >
                <SelectTrigger data-testid="select-item-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No category</SelectItem>
                  {categories.filter(c => !newItem.department || c.department === newItem.department).map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>
            <div>
              <Label>Selling Price (₹)</Label>
              <Input
                type="number"
                value={newItem.selling_price}
                onChange={(e) => setNewItem({ ...newItem, selling_price: e.target.value })}
                placeholder="0.00"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItem(false)}>Cancel</Button>
            <Button onClick={handleCreateItem} data-testid="submit-add-item">Create Item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Sale Item Dialog */}
      <Dialog open={showEditItem} onOpenChange={setShowEditItem}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Sale Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={newItem.name}
                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Code</Label>
              <Input
                value={newItem.code}
                onChange={(e) => setNewItem({ ...newItem, code: e.target.value })}
              />
            </div>
            <div>
              <Label>Department *</Label>
              <Select 
                value={newItem.department} 
                onValueChange={(v) => setNewItem({ ...newItem, department: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {DEPARTMENTS.map(d => (
                    <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category</Label>
              <Select 
                value={newItem.category} 
                onValueChange={(v) => setNewItem({ ...newItem, category: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No category</SelectItem>
                  {categories.filter(c => !newItem.department || c.department === newItem.department).map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
              />
            </div>
            <div>
              <Label>Selling Price (₹)</Label>
              <Input
                type="number"
                value={newItem.selling_price}
                onChange={(e) => setNewItem({ ...newItem, selling_price: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditItem(false)}>Cancel</Button>
            <Button onClick={handleUpdateItem}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recipe Editor Dialog */}
      <Dialog open={showRecipeEditor} onOpenChange={setShowRecipeEditor}>
        <DialogContent className="max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Beaker className="w-5 h-5" />
              Recipe for: {selectedItem?.name}
            </DialogTitle>
            <DialogDescription>
              Add ingredients from the Price Master to this sale item
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Ingredient Search */}
            {canManage && (
              <Card className="bg-gray-50">
                <CardContent className="pt-4">
                  <Label>Search Ingredients</Label>
                  <div className="flex gap-2 mt-1">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search price master items..."
                        value={ingredientSearch}
                        onChange={(e) => setIngredientSearch(e.target.value)}
                        className="pl-10"
                        data-testid="search-ingredients"
                      />
                    </div>
                    <Input
                      type="number"
                      placeholder="Qty"
                      value={newIngredient.quantity}
                      onChange={(e) => setNewIngredient({ ...newIngredient, quantity: e.target.value })}
                      className="w-20"
                      data-testid="ingredient-quantity"
                    />
                    <Input
                      placeholder="Unit"
                      value={newIngredient.unit}
                      onChange={(e) => setNewIngredient({ ...newIngredient, unit: e.target.value })}
                      className="w-20"
                    />
                  </div>
                  
                  {/* Search Results */}
                  {searchResults.length > 0 && (
                    <ScrollArea className="h-40 mt-2 border rounded-md">
                      <div className="p-2 space-y-1">
                        {searchResults.map((ing) => (
                          <div
                            key={ing.id}
                            className="flex items-center justify-between p-2 hover:bg-white rounded cursor-pointer"
                            onClick={() => addIngredientToRecipe(ing)}
                            data-testid={`ingredient-result-${ing.id}`}
                          >
                            <div>
                              <p className="font-medium text-sm">{ing.item_name}</p>
                              <p className="text-xs text-gray-500">
                                {ing.category_name} • {ing.company || 'No brand'} • {ing.unit}
                              </p>
                            </div>
                            <Plus className="w-4 h-4 text-blue-600" />
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Current Recipe */}
            <div>
              <Label className="mb-2 block">Current Recipe ({currentRecipe.length} ingredients)</Label>
              {currentRecipe.length === 0 ? (
                <div className="text-center py-6 text-gray-500 border rounded-md">
                  <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No ingredients added yet</p>
                </div>
              ) : (
                <ScrollArea className="h-[200px] border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Ingredient</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead className="w-24">Qty</TableHead>
                        <TableHead className="w-20">Unit</TableHead>
                        {canManage && <TableHead className="w-12"></TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {currentRecipe.map((ing) => (
                        <TableRow key={ing.item_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm">{ing.item_name}</p>
                              <p className="text-xs text-gray-500">{ing.company || '-'}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">{ing.category_name}</TableCell>
                          <TableCell>
                            {canManage ? (
                              <Input
                                type="number"
                                value={ing.quantity}
                                onChange={(e) => updateIngredientQuantity(ing.item_id, e.target.value)}
                                className="h-8 w-20"
                              />
                            ) : (
                              ing.quantity
                            )}
                          </TableCell>
                          <TableCell className="text-sm">{ing.unit}</TableCell>
                          {canManage && (
                            <TableCell>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 h-8 w-8 p-0"
                                onClick={() => removeIngredientFromRecipe(ing.item_id)}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecipeEditor(false)}>
              Cancel
            </Button>
            {canManage && (
              <Button onClick={saveRecipe} data-testid="save-recipe-btn">
                <Save className="w-4 h-4 mr-2" />
                Save Recipe
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Category Dialog */}
      <Dialog open={showAddCategory} onOpenChange={setShowAddCategory}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Sale Item Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Category Name *</Label>
              <Input
                value={newCategory.name}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                placeholder="e.g., Starters, Main Course"
                data-testid="input-category-name"
              />
            </div>
            <div>
              <Label>Department *</Label>
              <Select 
                value={newCategory.department} 
                onValueChange={(v) => setNewCategory({ ...newCategory, department: v })}
              >
                <SelectTrigger data-testid="select-category-department">
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {DEPARTMENTS.map(d => (
                    <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCategory(false)}>Cancel</Button>
            <Button onClick={handleCreateCategory} data-testid="submit-add-category">Create Category</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
